import { batchGetProducts, BatchGetProductsApiParameter } from "@dana/Services/DanaProductService";
import { getDanaGeneralSettings } from "@dana/Store/Context/ContextSelectors";
import ApplicationStateDana from "@dana/Store/DanaApplicationState";
import { getProductCollectionV2 } from "@insite/client-framework/Services/ProductServiceV2";
import siteMessage from "@insite/client-framework/SiteMessage";
import { UploadError } from "@insite/client-framework/Store/Components/OrderUpload/Handlers/BatchLoadProducts";
import translate from "@insite/client-framework/Translate";
import { ProductDto, ProductModel } from "@insite/client-framework/Types/ApiModels";
import Search from "@insite/mobius/Icons/Search";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import debounce from "lodash/debounce";
import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import styled from "styled-components";
import { danaUploadErrorMessages, EditableRow } from "@dana/Components/DanaOrderUpload/DanaOrderUploadTypes";
import { themeBackgrounds, themeColors } from "@dana/MobiusOverrides/styles/colors";
import { fontSizes, fontWeights, lineHeights } from "@dana/MobiusOverrides/styles/typography";

const SearchWrapper = styled.div`
    position: relative;
`;

const SearchInputRow = styled.div<{ hasError?: boolean }>`
    display: flex;
    align-items: center;
    border: 1px solid ${p => p.hasError ? themeColors.danger : themeColors.primary.black};
    border-radius: 4px;
    background: ${p => p.hasError ? themeBackgrounds.danger : themeBackgrounds.white};
   	padding: 8px 16px;
    gap: 6px;
    width: 360px;

    svg {
        color: ${themeColors.primary.richBlack};
        flex-shrink: 0;
        width: 18px;
        height: 18px;
    }
`;

const SearchInput = styled.input<{ hasError?: boolean }>`
    flex: 1;
    border: none;
    background: transparent;
	padding: 0;
	font-size: ${fontSizes.medium};
	line-height: ${lineHeights.medium};
    color: ${p => p.hasError ? themeColors.danger : "inherit"};
    outline: none;
    min-width: 0;

    &::placeholder {
        color: ${p => p.hasError ? themeBackgrounds.danger : "inherit"};
    }
`;

const Dropdown = styled.div`
    position: absolute;
    top: 100%;
    left: 0;
    background: ${themeBackgrounds.white};
    border: 1px solid ${themeBackgrounds.lightGray};
    z-index: 300;
    width: 360px;
    max-height: 205px;
    overflow-y: auto;
`;

const DropdownHeader = styled.div`
    padding: 4px 0 4px 11px;
    font-size: ${fontSizes.xSmall};
    font-weight: ${fontWeights.bold};
    text-transform: uppercase;
    background: ${themeBackgrounds.offWhite};
    border-bottom: 1px solid ${themeBackgrounds.lightGray};
`;

const DropdownItem = styled.div`
    padding: 4px 0 4px 11px;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    border: 1px solid ${themeBackgrounds.lightGray};
    &:last-child {
        border-bottom: none;
    }

    &:hover {
        background: rgba(0,113,173,0.19);
    }
`;

const DropdownPartNo = styled.span`
    font-weight: ${fontWeights.bold};
    color: ${themeBackgrounds.black};
    font-size: ${fontSizes.small};
`;

const DropdownPartName = styled.span`
    color: ${themeBackgrounds.darkGray};
    font-size: ${fontSizes.xSmall};
`;

const DropdownReplacementItem = styled.div`
    padding: 6px 11px;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    background: #fffbf5;
    border-bottom: 1px solid ${themeBackgrounds.lightGray};

    &:hover {
        background: #fff0d0;
    }
`;

const DropdownReplacementLabel = styled.span`
    font-size: ${fontSizes.xSmall};
    color: #b45309;
    font-weight: ${fontWeights.bold};
    margin-bottom: 2px;
`;

const DropdownReplacementDesc = styled.span`
    font-size: ${fontSizes.xSmall};
    color: ${themeBackgrounds.darkGray};
`;

const DropdownEmpty = styled.div`
    padding: 4px 0 4px 11px;
    color: ${themeBackgrounds.darkGray};
    font-size: ${fontSizes.xSmall};
`;

const DropdownLoading = styled.div`
    padding: 4px 0 4px 11px;
    color: ${themeBackgrounds.darkGray};
    font-size: ${fontSizes.xSmall};
`;

const ChevronIcon = styled.span<{ isOpen: boolean }>`
    display: flex;
    align-items: center;
    flex-shrink: 0;
    transition: transform 0.2s ease;
    transform: ${p => p.isOpen ? "rotate(180deg)" : "rotate(0deg)"};
    svg {
        width: 14px;
        height: 14px;
        color: ${themeColors.primary.richBlack};
    }
`;

interface SearchResult {
    partNumber: string;
    partName: string;
    product: ProductModel;
    isReplacementSuggestion?: boolean;
    replacedPartNumber?: string;
}

const mapStateToProps = (state: ApplicationStateDana) => ({
    danaGeneralSettings: getDanaGeneralSettings(state),
    uploadErrorMessages: danaUploadErrorMessages

});
interface PartNumberSearchCellOwnProps {
    row: EditableRow;
    onSelectProduct: (rowId: string, result: SearchResult, validatedProduct: any, replacementPart?: any) => void;
    onSelectInvalid: (rowId: string, errorMessage?: string, replacementPart?: ProductDto) => void;
}

type PartNumberSearchReduxProps = ReturnType<typeof mapStateToProps>;
type PartNumberSearchCellProps = PartNumberSearchCellOwnProps & PartNumberSearchReduxProps;
const PartNumberSearchCell: FC<PartNumberSearchCellProps> = ({
    row,
    onSelectProduct,
    onSelectInvalid,
    danaGeneralSettings,
    uploadErrorMessages

}) => {

    const [searchTerm, setSearchTerm] = useState(row.partNumber);
    const [results, setResults] = useState<SearchResult[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const [validationError, setValidationError] = useState<string | null>(null);
    const selectedRef = useRef(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const discontinuedLabel = danaGeneralSettings?.partsObsoleteDiscontinuedStatusCode
        ?.toLowerCase()
        .split(",")
        .map((s: any) => s.trim());
    const hasError = row.status === "invalid";

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        setSearchTerm(row.partNumber);
        setValidationError(null);
    }, [row.partNumber]);

    const getReplacementPart = (
        replcementPartId: string,
        mappedResult: SearchResult[]
    ) => {
        if (!replcementPartId) return undefined;

        return mappedResult.find(
            r =>
                r.product?.id === replcementPartId
        )?.product;
    };

    const fetchProducts = useCallback(
        debounce(async (term: string) => {
            if (!term.trim() || term.trim().length < 2) {
                setResults([]);
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            try {
                const response = await getProductCollectionV2({
                    search: term.trim(),
                    pageSize: 8,
                });
                const mapped: SearchResult[] = (response?.products ?? []).map(p => ({
                    partNumber: p.productNumber ?? "",
                    partName: p.productTitle ?? "",
                    product: p,
                } as any));

                const withReplacements: SearchResult[] = [];
                for (const result of mapped) {
                    const replacementProduct = result.product.detail?.replacementProductId ? getReplacementPart(result.product.detail?.replacementProductId, mapped) : undefined;

                    if (replacementProduct) {
                        withReplacements.push({
                            partNumber: (replacementProduct as any).modelNumber ?? (replacementProduct as any).productNumber ?? "",
                            partName: (replacementProduct as any).shortDescription ?? (replacementProduct as any).productTitle ?? "",
                            product: replacementProduct as any,
                            isReplacementSuggestion: true,
                            replacedPartNumber: result.partNumber,
                        });
                    }
                    withReplacements.push(result);
                }

                setResults(withReplacements);
                setIsOpen(withReplacements.length > 0 || term.trim().length >= 2);
            } catch {
                setResults([]);
            } finally {
                setIsLoading(false);
            }
        }, 300),
        [],
    );

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setSearchTerm(val);
        selectedRef.current = false;
        setValidationError(null);
        setIsLoading(true);
        setIsOpen(true);
        fetchProducts(val);
    };

    const handleSelect = async (result: SearchResult) => {
        setValidationError(null);
        setIsOpen(false);
        setResults([]);
        setIsValidating(true);
        try {
            setSearchTerm(result.partNumber);
            const selectedProduct = await batchGetProducts({
                extendedNames: [result.partNumber],
            } as BatchGetProductsApiParameter).then(r => r[0]);
         
            const error = validateProduct(selectedProduct, discontinuedLabel);
          
            if (selectedProduct && error === UploadError.OutOfStock) {
                const replacementId = (selectedProduct as any).detail?.replacementProductId;
                const replacementResult = results.find(r => r.product?.id === replacementId);
                const replacementPartNumber = replacementResult?.partNumber ?? replacementId;
                const obsoleteMsg = replacementPartNumber
                    ? `${result.partNumber} ${translate("is obsolete and has been replaced by")} ${replacementPartNumber}`
                    : `${result.partNumber} ${translate("is obsolete")}`;
                setValidationError(obsoleteMsg);
                const replacementProductDto = replacementResult?.product as ProductDto | undefined;
                onSelectInvalid(row.id, obsoleteMsg, replacementProductDto);
            } else if (selectedProduct && error === UploadError.None) {
                selectedRef.current = true;
                const replacementPart = result.isReplacementSuggestion ? result.product : undefined;
                onSelectProduct(row.id, result, selectedProduct, replacementPart);
            } else {
                const errorMsg = siteMessage(uploadErrorMessages[error])?.toString();
                setValidationError(errorMsg);
                onSelectInvalid(row.id, errorMsg);
            }
        } catch {
            const errorMsg = siteMessage("Dana_WishList_PartUpload_ValidationError") as string;
            setValidationError(errorMsg);
            onSelectInvalid(row.id, errorMsg);
        } finally {
            setIsValidating(false);
        }
    };
    const validateProduct = (product: ProductDto | null, discontinuedLabel: string[] | any) => {
        let isAbsolute = false;
        if (product) {
            const showAddToCartSection = ('properties' in product) ?
                (product.properties as { danaStatusCode?: string })?.danaStatusCode : "";

            if (showAddToCartSection) {
                isAbsolute = discontinuedLabel?.includes(showAddToCartSection?.toLowerCase());
            }
            if (
                !('properties' in product)
                || isAbsolute
            ) {
                return UploadError.OutOfStock;
            }
            if (
                !('properties' in product) ||
                (product.properties as { showRtbCodeAddToCart?: string })?.showRtbCodeAddToCart !== "true"
                || isAbsolute
            ) {
                return UploadError.Unavailable;
            }

            if (product.canConfigure || (product.isConfigured && !product.isFixedConfiguration)) {
                return UploadError.ConfigurableProduct;
            }

            if (product.isStyleProductParent) {
                return UploadError.StyledProduct;
            }

            if (!product.canAddToCart) {
                return UploadError.Unavailable;
            }

        } else {
            return UploadError.Unavailable;
        }

        return UploadError.None;
    };


    const handleBlur = () => {
        if (!selectedRef.current) {
            setSearchTerm(row.partNumber);
            setValidationError(null);
        }
        setIsOpen(false);
    };

    const handleFocus = () => {
        selectedRef.current = false;
        setValidationError(null);
        if (searchTerm.trim().length >= 2) {
            setIsOpen(true);
            fetchProducts(searchTerm);
        }
    };

    return (
        <SearchWrapper ref={dropdownRef}>
            <SearchInputRow hasError={hasError}>
                <Search />
                <SearchInput
                    hasError={hasError}
                    ref={inputRef}
                    value={searchTerm}
                    onChange={handleChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    placeholder={translate("Search part number…")}
                    data-test-selector={`danaOrderUpload_partSearch_${row.rowNumber}`}
                    autoComplete="off"
                    disabled={isValidating}
                />
                {isValidating
                    ? <DropdownLoading style={{ padding: 0, fontSize: 11 }}>{translate("Validating…")}</DropdownLoading>
                    : <ChevronIcon isOpen={isOpen}><ChevronDown /></ChevronIcon>
                }
            </SearchInputRow>
            {isOpen && (
                <Dropdown>
                    <DropdownHeader>
                        {translate("Select a product")}
                    </DropdownHeader>
                    {isLoading ? (
                        <DropdownLoading>{translate("Searching…")}</DropdownLoading>
                    ) : results.length === 0 ? (
                        <DropdownEmpty>{translate("No products found.")}</DropdownEmpty>
                    ) : (
                        results.map((r, i) => (
                            r.isReplacementSuggestion ? (
                                <DropdownReplacementItem
                                    key={`replacement-${r.replacedPartNumber}-${i}`}
                                    onMouseDown={() => handleSelect(r)}
                                >
                                    <DropdownReplacementLabel>
                                        ⚠ {r.replacedPartNumber} {translate("is replaced by")} {r.partNumber}
                                    </DropdownReplacementLabel>
                                    <DropdownReplacementDesc>{r.partName}</DropdownReplacementDesc>
                                </DropdownReplacementItem>
                            ) : (
                                <DropdownItem
                                    key={r.partNumber}
                                    onMouseDown={() => handleSelect(r)}
                                >
                                    <DropdownPartNo>{r.partNumber}</DropdownPartNo>
                                    <DropdownPartName>{r.partName}</DropdownPartName>
                                </DropdownItem>
                            )
                        ))
                    )}
                </Dropdown>
            )}
        </SearchWrapper>
    );
};


export default connect(mapStateToProps)(PartNumberSearchCell);

